import React, { useEffect, useState, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { 
  ChevronLeft, 
  ChevronRight, 
  FlaskConical, 
  Building2 
} from "lucide-react";
import axios from "axios";

import TestCard from "../LabTest/TestCard";
import ReplaceCartPopup from "../LabTest/ReplaceCartPopup";
import { useCartActions } from "../../hooks/useCartActions";
import { getAllTests } from "../../services/LabTestApi/PopularTest";
import { useLocationContext } from "../../context/LocationContext";
import { 
  fetchWishlist, 
  addToWishlist, 
  removeFromWishlist,
  addWishlistOptimistic,
  removeWishlistOptimistic,
} from "../../Redux/wishlistSlice";

export default function PopularTest() {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { coords, selectedCity } = useLocationContext();
  const cart = useSelector((state) => state.cart.items || []);
  const wishlistItems = useSelector((state) => state.wishlist.items || []);

  const { addWithReplaceCheck, removeItem, LoginPopupComponent } = useCartActions();

  const [tests, setTests] = useState([]);
  const [popularLabs, setPopularLabs] = useState([]);
  const [selectedLabId, setSelectedLabId] = useState("all");
  const [loading, setLoading] = useState(true);

  const testSliderRef = useRef(null);

  // ========== WISHLIST ==========
  const getUserId = () => {
    try {
      const userData = JSON.parse(localStorage.getItem("medarphaUser") || "{}");
      return userData?.userId || null;
    } catch {
      return null;
    }
  };

  const isInWishlist = (productId, categoryId = 2) => {
    if (!wishlistItems || !Array.isArray(wishlistItems)) return false;
    return wishlistItems.some(
      (item) =>
        Number(item.productId) === Number(productId) &&
        Number(item.categoryId) === Number(categoryId)
    );
  };

  const handleWishlistToggle = async (test, e) => {
    e.stopPropagation();
    const userId = getUserId();
    const testId = test?.testID || test?.TestID || test?.id;
    if (!userId) {
      window.dispatchEvent(new CustomEvent("trigger-login-popup"));
      return;
    }

    const isWished = isInWishlist(testId, 2);
    if (isWished) {
      dispatch(removeWishlistOptimistic({ productId: testId, categoryId: 2 }));
    } else {
      dispatch(addWishlistOptimistic({ productId: testId, categoryId: 2 }));
    }

    try {
      if (isWished) {
        await dispatch(removeFromWishlist({ userId, productId: testId, categoryId: 2 })).unwrap();
      } else {
        await dispatch(addToWishlist({ userId, productId: testId, categoryId: 2 })).unwrap();
      }
    } catch (err) {
      console.error("Wishlist error:", err);
    }
  };

  // ========== LOAD TESTS & LABS ==========
  useEffect(() => {
    let isMounted = true;

    const loadData = async () => {
      try {
        setLoading(true);

        const [labsRes, testRes] = await Promise.all([
          axios.get("https://admin.medrphalabs.com/api/Lab").catch(() => ({ data: [] })),
          getAllTests(1, 1000).catch(() => ({ data: [] })),
        ]);

        let labsList = [];
        if (Array.isArray(labsRes.data)) labsList = labsRes.data;
        else if (Array.isArray(labsRes.data?.data)) labsList = labsRes.data.data;

        const testData = Array.isArray(testRes?.data) ? testRes.data : (Array.isArray(testRes) ? testRes : []);

        const userLat = coords?.lat;
        const userLng = coords?.lng;

        const mappedTests = testData.map((t) => {
          const lObj = labsList.find((l) => Number(l.id) === Number(t.LabId || t.labId));
          const labName =
            lObj?.name ||
            lObj?.labName ||
            lObj?.lab_name ||
            (t.LabId === 1 || t.labId === 1 ? "DGchem Lab" : t.LabId === 3 || t.labId === 3 ? "DOCOPD LAB" : "Certified Partner Lab");

          return {
            ...t,
            labName: labName,
            labId: t.LabId || t.labId,
          };
        });

        let filteredTests = mappedTests.filter(t => {
          if (!userLat || !userLng) return true;
          let tLat = Number(t.latitude || t.Latitude || t.lat || t.Lat);
          let tLng = Number(t.longitude || t.Longitude || t.Longnitude || t.longnitude || t.lng || t.Lng);
          
          if (isNaN(tLat) || isNaN(tLng) || !tLat || !tLng) {
            const matchedLab = labsList.find(l => Number(l.id) === Number(t.LabId || t.labId));
            if (matchedLab) {
              tLat = Number(matchedLab.latitude || matchedLab.Latitude || matchedLab.lat || matchedLab.Lat);
              tLng = Number(matchedLab.longitude || matchedLab.Longitude || matchedLab.Longnitude || matchedLab.longnitude || matchedLab.lng || matchedLab.Lng);
            }
          }

          if (isNaN(tLat) || isNaN(tLng) || !tLat || !tLng) return false;
          
          // Calculate 20km distance
          const R = 6371;
          const dLat = ((Number(userLat) - tLat) * Math.PI) / 180;
          const dLon = ((Number(userLng) - tLng) * Math.PI) / 180;
          const a =
            Math.sin(dLat / 2) ** 2 +
            Math.cos((tLat * Math.PI) / 180) *
              Math.cos((Number(userLat) * Math.PI) / 180) *
              Math.sin(dLon / 2) ** 2;
          const distKm = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a)) * R;
          return distKm <= 20;
        });

        if (isMounted) {
          const finalTests = filteredTests.length > 0 ? filteredTests : mappedTests;
          setTests(finalTests);

          // Get unique labs from tests
          const uniqueLabIds = [...new Set(finalTests.map((t) => t.labId).filter(Boolean))];
          const labsForTests = uniqueLabIds.map((lId) => {
            const lObj = labsList.find((l) => Number(l.id) === Number(lId));
            return {
              id: lId,
              name: lObj?.name || lObj?.labName || (lId === 1 ? "DGchem Lab" : lId === 3 ? "DOCOPD LAB" : `Lab #${lId}`),
            };
          });
          setPopularLabs(labsForTests);
        }
      } catch (err) {
        console.error("Error loading tests on HomePage:", err);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    loadData();
    return () => {
      isMounted = false;
    };
  }, [coords?.lat, coords?.lng, selectedCity]);

  const slideTests = (direction) => {
    if (testSliderRef.current) {
      const scrollAmount = 300;
      testSliderRef.current.scrollBy({
        left: direction === "left" ? -scrollAmount : scrollAmount,
        behavior: "smooth",
      });
    }
  };

  const isTestInCart = (testId) => {
    return cart.some(
      (item) => Number(item.productId) === Number(testId) && Number(item.categoryId) === 2
    );
  };

  const handleTestCartToggle = async (test) => {
    const testId = test.testID || test.testId || test.id;
    if (isTestInCart(testId)) {
      removeItem(testId, 2);
    } else {
      await addWithReplaceCheck({
        productId: testId,
        name: test.testName || test.name,
        price: test.sellingPrice || test.testPrice || test.price || 0,
        discountedPrice: test.discount || 0,
        categoryId: 2,
        storeId: test.labId || 1,
        image: test.testImage || "",
      });
    }
  };

  const dynamicTests =
    selectedLabId === "all"
      ? tests
      : tests.filter((t) => Number(t.labId) === Number(selectedLabId));

  if (!loading && tests.length === 0) {
    return null;
  }

  return (
    <section className="w-full my-6">
      <div className="bg-white border border-slate-200/90 rounded-3xl p-4 sm:p-6 md:p-8 shadow-xs relative">
        
        {/* Header with Title and Sliding Controls */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-5">
          <div className="flex items-center gap-2.5">
            <div className="w-2 h-7 bg-gradient-to-b from-red-600 to-rose-600 rounded-full" />
            <div>
              <h3 className="text-lg sm:text-xl md:text-2xl font-black text-slate-900 leading-tight">
                Diagnostic Blood & Lab Tests
              </h3>
              <p className="text-xs text-slate-500 font-medium mt-0.5">
                Showing {dynamicTests.length} tests available at nearby certified labs
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 self-end sm:self-auto">
            {/* Slider Arrow Buttons */}
            <button
              onClick={() => slideTests("left")}
              className="w-9 h-9 rounded-full bg-slate-100 hover:bg-red-600 hover:text-white text-slate-700 flex items-center justify-center transition-all shadow-2xs cursor-pointer"
              title="Slide Left"
            >
              <ChevronLeft size={18} />
            </button>
            <button
              onClick={() => slideTests("right")}
              className="w-9 h-9 rounded-full bg-slate-100 hover:bg-red-600 hover:text-white text-slate-700 flex items-center justify-center transition-all shadow-2xs cursor-pointer"
              title="Slide Right"
            >
              <ChevronRight size={18} />
            </button>

            <button
              onClick={() => navigate("/all-tests")}
              className="text-xs font-bold text-red-600 hover:text-red-700 bg-red-50 hover:bg-red-100 px-3.5 py-2 rounded-xl transition-all flex items-center gap-1 ml-1 cursor-pointer"
            >
              <span>View All</span>
              <ChevronRight size={14} />
            </button>
          </div>
        </div>

        {/* Dynamic Lab Filter Chips */}
        {popularLabs.length > 0 && (
          <div className="flex items-center gap-2 overflow-x-auto no-scrollbar pb-3 mb-3">
            <button
              onClick={() => setSelectedLabId("all")}
              className={`text-xs font-bold px-3.5 py-1.5 rounded-xl transition-all whitespace-nowrap border flex items-center gap-1.5 cursor-pointer ${
                selectedLabId === "all"
                  ? "bg-red-600 text-white border-red-600 shadow-xs"
                  : "bg-slate-50 text-slate-700 border-slate-200 hover:border-red-300"
              }`}
            >
              <span>All Popular Labs ({tests.length})</span>
            </button>

            {popularLabs.map((lab) => {
              const labTestCount = tests.filter((t) => Number(t.labId) === Number(lab.id)).length;
              return (
                <button
                  key={lab.id}
                  onClick={() => setSelectedLabId(lab.id)}
                  className={`text-xs font-bold px-3.5 py-1.5 rounded-xl transition-all whitespace-nowrap border flex items-center gap-1.5 cursor-pointer ${
                    Number(selectedLabId) === Number(lab.id)
                      ? "bg-red-600 text-white border-red-600 shadow-xs"
                      : "bg-slate-50 text-slate-700 border-slate-200 hover:border-red-300"
                  }`}
                >
                  <Building2
                    size={12}
                    className={Number(selectedLabId) === Number(lab.id) ? "text-white" : "text-slate-400"}
                  />
                  <span>
                    {lab.name} ({labTestCount})
                  </span>
                </button>
              );
            })}
          </div>
        )}

        {/* Dynamic Test Cards SLIDER */}
        {loading ? (
          <div className="flex items-center gap-4 overflow-hidden py-2">
            {[1, 2, 3, 4].map((i) => (
              <div
                key={i}
                className="w-[240px] sm:w-[260px] shrink-0 bg-slate-50 rounded-2xl p-4 border border-slate-200 animate-pulse space-y-3"
              >
                <div className="h-36 bg-slate-200 rounded-xl w-full" />
                <div className="h-4 bg-slate-200 rounded w-3/4" />
                <div className="h-3 bg-slate-100 rounded w-1/2" />
                <div className="h-9 bg-slate-200 rounded-xl w-full" />
              </div>
            ))}
          </div>
        ) : dynamicTests.length === 0 ? (
          <div className="bg-slate-50 rounded-2xl p-8 text-center border border-slate-200 my-2">
            <FlaskConical size={36} className="text-slate-300 mx-auto mb-2" />
            <p className="text-xs font-bold text-slate-700">No diagnostic tests found for selected lab.</p>
            <button
              onClick={() => setSelectedLabId("all")}
              className="mt-3 text-xs font-bold text-red-600 bg-white border border-red-200 px-3.5 py-1.5 rounded-lg cursor-pointer hover:bg-red-50"
            >
              View All Labs Tests
            </button>
          </div>
        ) : (
          <div
            ref={testSliderRef}
            className="flex items-stretch gap-4 overflow-x-auto no-scrollbar pb-3 pt-1 scroll-smooth"
          >
            {dynamicTests.map((test, idx) => (
              <TestCard
                key={test.testID || test.testId || idx}
                test={test}
                inCart={isTestInCart(test.testID || test.testId)}
                onCartToggle={handleTestCartToggle}
                inWishlist={isInWishlist(test.testID || test.testId, 2)}
                onWishlistToggle={handleWishlistToggle}
                className="w-[240px] sm:w-[260px] flex-shrink-0"
              />
            ))}
          </div>
        )}
      </div>

      <ReplaceCartPopup />
      {LoginPopupComponent}
    </section>
  );
}
